import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  SxProps,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { capitalize, clone, cloneDeep, isEqual } from "lodash-es";
import { add, format, nextMonday, startOfDay } from "date-fns";
import { ElementItemProps } from "../types";
import ElementFrame from "./ElementFrame";
import { useDevice } from "../../../../utils/DeviceProvider";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";

import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";

interface OPTTV12Parameters {
  version: 1 | 2;
}

interface PatrolTimetableValueBlock {
  checked: boolean | null;
  block?: number;
  tags?: number;
  interval?: number;
  offset?: number;
  random?: number;
}
interface PatrolTimetableValue {
  combo: string;
  value: PatrolTimetableValueBlock[][];
}

interface SelectedBlock {
  day: number;
  block: number;
  value: PatrolTimetableValueBlock;
  title: string;
}

const OPTTV12 = ({ version, configKey, disabled }: Omit<ElementItemProps<OPTTV12Parameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<PatrolTimetableValue | null>(null);
  const [value, setValue] = useState<PatrolTimetableValue["value"] | null>(null);
  const [comboValue, setComboValue] = useState<string | null>(null);
  const [narrow, setNarrow] = useState<boolean | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<SelectedBlock | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const temporaryComboValue = useRef<null | string>(null);

  const { formatMessage } = useIntl();

  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const editedStyle = useEditedStyles();
  const { text: editedText } = useEditedColor();
  const theme = useTheme();

  const comboItems = useMemo(
    () => [
      { value: "all-unique", label: formatMessage({ defaultMessage: "All days are unique" }) },
      { value: "all-same", label: formatMessage({ defaultMessage: "All days are same" }) },
      { value: "weekdays+weekend", label: formatMessage({ defaultMessage: "Weekdays + weekend days are same" }) },
      { value: "weekdays", label: formatMessage({ defaultMessage: "Weekdays are same" }) },
      { value: "weekend", label: formatMessage({ defaultMessage: "Weekend days are same" }) },
    ],
    [formatMessage]
  );
  const defaultValue = useMemo(
    () => ({
      combo: "all-unique",
      value: Array.from({ length: 7 }).map(() => Array.from({ length: 48 }).map(() => ({ checked: false }))),
    }),
    []
  );
  const days = useMemo(() => Array.from({ length: 7 }).map((_, i) => format(add(nextMonday(new Date()), { days: i }), "EEEEEE")), []);
  const blocks = useMemo(() => Array.from({ length: 48 }).map((_, i) => format(add(startOfDay(new Date()), { minutes: i * 30 }), "HH:mm")), []);

  const classNames = useMemo<{ timeLabelContainer: SxProps; checkboxContainer: SxProps; dayLabels: SxProps; checkbox: SxProps }>(
    () => ({
      timeLabelContainer: {
        margin: 0,
        padding: 0,
        width: narrow || version === 1 ? 45 : 50,
        height: narrow ? 28 : "auto",
        display: "flex",
        flexDirextion: "row",
        alignItems: "flex-end",
        justifyContent: "center",
      },
      checkboxContainer: {
        width: version === 1 ? (narrow ? 30 : 45) : 50,
        display: "flex",
        flexDirextion: "row",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBox-root": {
          display: "flex",
          flexDirextion: "row",
          alignItems: "center",
          justifyContent: "center",
          "&:hover":
            version === 1
              ? {}
              : {
                  backgroundColor: theme.palette.grey[200],
                },
        },
      },
      dayLabels: {
        width: narrow ? (version === 1 ? 30 : 50) : "auto",
        height: 28,
        display: "flex",
        flexDirextion: "row",
        alignItems: "center",
        justifyContent: narrow ? "center" : "flex-end",
      },
      checkbox: {
        margin: 0,
        padding: 0,
      },
    }),
    [version, theme, narrow]
  );

  const onChange = useCallback(
    (value: PatrolTimetableValueBlock[][], comboValue: string) => {
      if (configIndex === null) {
        return;
      }
      currentValue[configKey].items[configIndex].value = { combo: comboValue, value };
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  const copyDays = useCallback(
    (value: PatrolTimetableValueBlock[][], dayIndex: number, comboValue: string) => {
      if (dayIndex === 0 && ["all-same", "weekdays+weekend", "weekdays"].includes(comboValue)) {
        for (let i = 1; i < (comboValue === "all-same" ? 7 : 5); i++) {
          value[i] = clone(value[dayIndex]);
        }
      } else if (dayIndex === 5 && ["weekdays+weekend", "weekend"].includes(comboValue)) {
        value[dayIndex + 1] = clone(value[dayIndex]);
      }
      onChange(value, comboValue);
    },
    [onChange]
  );

  const handleDialogChange = useCallback(
    (dayIndex: number, blockIndex: number, newValue: PatrolTimetableValueBlock) => {
      if (!value || !comboValue || version === 1) {
        return;
      }
      const oldBlock = value[dayIndex][blockIndex].block ?? 1;
      const { block, tags, interval, offset, random } = newValue;
      if ((block === 1 || block === undefined) && tags === undefined && interval === undefined && offset === undefined && random === undefined) {
        value[dayIndex][blockIndex] = { checked: true };
      } else {
        value[dayIndex][blockIndex] = {
          checked: null,
          block,
          ...Object.fromEntries(
            [
              ["tags", tags],
              ["interval", interval],
              ["offset", offset],
              ["random", random],
            ].filter(([_, value]) => value !== undefined)
          ),
        };
      }
      for (let i = blockIndex + 1; i < Math.min(blockIndex + (newValue.block || 1), 48); i++) {
        value[dayIndex][i] = { checked: null };
      }
      for (let i = blockIndex + (newValue.block || 1); i < Math.min(blockIndex + oldBlock, 48); i++) {
        value[dayIndex][i] = { checked: false };
      }
      copyDays(value, dayIndex, comboValue);
      onChange(value, comboValue);
    },
    [version, value, comboValue, onChange, copyDays]
  );

  const handleCheckChange = useCallback(
    (dayIndex: number, blockIndex: number, newValue: PatrolTimetableValueBlock) => {
      if (!value || !comboValue) {
        return;
      }
      if (version === 2) {
        const oldBlock = value[dayIndex][blockIndex].block ?? 1;
        for (let i = blockIndex + 1; i < Math.min(blockIndex + oldBlock, 48); i++) {
          value[dayIndex][i] = { checked: false };
        }
      }
      const { checked } = newValue;
      value[dayIndex][blockIndex] = { checked };
      copyDays(value, dayIndex, comboValue);
    },
    [version, value, comboValue, copyDays]
  );

  const clearAll = useCallback(() => {
    if (!comboValue) {
      return;
    }
    const emptyValue = cloneDeep(defaultValue.value);
    setValue(emptyValue);
    onChange(emptyValue, comboValue);
  }, [comboValue, defaultValue, onChange]);

  const applyComboChange = useCallback(() => {
    if (!value || !temporaryComboValue.current) {
      return;
    }
    setComboValue(temporaryComboValue.current);
    switch (temporaryComboValue.current) {
      case "all-same":
      case "weekdays+weekend":
      case "weekdays":
        copyDays(value, 0, temporaryComboValue.current);
        break;
    }
    switch (temporaryComboValue.current) {
      case "weekdays+weekend":
      case "weekend":
        copyDays(value, 5, temporaryComboValue.current);
        break;
    }
    onChange(value, temporaryComboValue.current as string);
  }, [value, onChange, copyDays]);

  const handleCombo = useCallback(
    (e) => {
      if (!value) {
        return;
      }
      temporaryComboValue.current = e.target.value;
      if (e.target.value === "all-unique") {
        applyComboChange();
        return;
      }
      const intervals = [];
      if (e.target.value === "all-same") {
        intervals.push([1, 7]);
      } else if (e.target.value === "weekdays+weekend") {
        intervals.push([1, 5], [5, 7]);
      } else if (e.target.value === "weekdays") {
        intervals.push([1, 5]);
      } else if (e.target.value === "weekend") {
        intervals.push([5, 7]);
      }
      let warning = false;
      intervals.some(([min, max]) => {
        for (let i = min; i < max; i++) {
          warning = value[i].some(({ checked }) => {
            if (checked !== false) {
              return true;
            }
            return false;
          });
          if (warning) {
            return true;
          }
        }
        return warning;
      });

      if (warning) {
        setAlertDialogOpen(true);
      } else {
        applyComboChange();
      }
    },
    [value, applyComboChange]
  );

  const containerRefChanged = useCallback(
    (node) => {
      if (!node) {
        return;
      }
      setNarrow(node.offsetWidth < (version === 1 ? 1400 : 2500));
    },
    [version]
  );

  const openBlockDialog = useCallback((day, block, value) => {
    setSelectedBlock({
      day,
      block,
      value,
      title: `${capitalize(format(add(nextMonday(new Date()), { days: day }), "EEEE"))} ${format(
        add(startOfDay(new Date()), { minutes: block * 30 }),
        "HH:mm"
      )}`,
    });
    setBlockDialogOpen(true);
  }, []);
  const closeBlockDialog = useCallback(() => setBlockDialogOpen(false), []);

  const closeAlertDialog = useCallback(() => setAlertDialogOpen(false), []);
  const closeAlertAndChange = useCallback(() => {
    closeAlertDialog();
    applyComboChange();
  }, [closeAlertDialog, applyComboChange]);

  const resetAllValue = useCallback(() => {
    if (!config || configIndex === null) {
      return;
    }
    const saved = config.toolboxConfig[configKey].items[configIndex].value;
    currentValue[configKey].items[configIndex].value = { ...saved };
    setCurrentValue(currentValue);
  }, [config, configIndex, configKey, currentValue, setCurrentValue]);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type }: { type: string; key?: string }) => type === `OPTTV${version}`) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, version]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex]?.value ?? null;
    setSaved(svd || null);
    const current = currentValue[configKey].items[configIndex]?.value;
    setComboValue(current?.combo || defaultValue.combo);
    setValue(current?.value || cloneDeep(defaultValue.value));
  }, [active, currentValue, config, configIndex, configKey, defaultValue]);

  if (value === null) {
    return (
      <ElementFrame label={formatMessage({ defaultMessage: "Patrol timetable" })}>
        <Skeleton variant="rectangular" height={40} sx={{ width: "100%", borderRadius: 1 }} />
      </ElementFrame>
    );
  }

  if (configIndex === -1) {
    return (
      <ElementFrame label={formatMessage({ defaultMessage: "Patrol timetable" })}>
        <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>
      </ElementFrame>
    );
  }

  const edited = !isEqual(saved, { combo: comboValue, value });

  return (
    <>
      <ElementFrame label={formatMessage({ defaultMessage: "Patrol timetable" })} ref={containerRefChanged} sx={{ width: "100%" }} disabled={disabled}>
        {narrow !== null ? (
          <Grid container spacing={1}>
            <Grid item>
              <FormControl
                variant="filled"
                size="small"
                hiddenLabel
                sx={{ margin: 0, minWidth: 200 }}
                className={saved?.combo !== comboValue ? editedStyle.input : ""}
                disabled={disabled}
                fullWidth
              >
                <Select value={comboValue} onChange={handleCombo} sx={{ margin: 0 }}>
                  {comboItems.map(({ value, label }) => (
                    <MenuItem key={`option-${value}`} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Button startIcon={<ClearIcon />} disabled={disabled} onClick={clearAll} color="error" sx={{ alignSelf: "flex-start" }}>
                {formatMessage({ defaultMessage: "Clear all" })}
              </Button>
            </Grid>
            {edited ? (
              <Grid item>
                <Button disabled={disabled} onClick={resetAllValue} sx={{ alignSelf: "flex-start", color: editedText }}>
                  {formatMessage({ defaultMessage: "Reset all" })}
                </Button>
              </Grid>
            ) : null}
            <Grid item xs={12} container spacing={0.5} sx={{ flexWrap: "nowrap", flexDirection: narrow ? "column" : "row" }}>
              <Grid item container spacing={0.5} key="DAYS" sx={{ flexDirection: narrow ? "row" : "column" }}>
                <Grid item sx={narrow ? classNames.timeLabelContainer : classNames.dayLabels} />
                {days.map((day, index) => (
                  <Grid item key={`day-${index}`} sx={classNames.dayLabels}>
                    <Typography variant="body2" component="span">
                      {day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              {blocks.map((block, blockIndex) => (
                <Grid item container spacing={0.5} key={`block-${blockIndex}`}>
                  <Grid item sx={classNames.timeLabelContainer}>
                    <Typography variant="body2">{block}</Typography>
                  </Grid>
                  <TimeTableBlock
                    blockIndex={blockIndex}
                    classNames={classNames}
                    openBlockDialog={openBlockDialog}
                    value={value}
                    saved={saved?.value ?? null}
                    comboValue={comboValue}
                    version={version}
                    disabled={disabled}
                    onChange={handleCheckChange}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : null}
      </ElementFrame>
      {selectedBlock ? (
        <BlockDialog
          open={blockDialogOpen}
          onClose={closeBlockDialog}
          selectedBlock={selectedBlock}
          onChange={(value) => handleDialogChange(selectedBlock.day, selectedBlock.block, value)}
        />
      ) : null}
      <Dialog open={alertDialogOpen} onClose={closeAlertDialog} sx={{ ".MuiDialogTitle-root": { backgroundColor: theme.palette.warning.main } }}>
        <DialogTitle>{formatMessage({ defaultMessage: "Warning!" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{formatMessage({ defaultMessage: "There are modifications that may be lost." })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeAlertDialog}>
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button color="warning" onClick={closeAlertAndChange}>
            {formatMessage({ defaultMessage: "Change it" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TimeTableBlock = memo(
  ({
    blockIndex,
    classNames,
    value,
    saved,
    version,
    comboValue,
    disabled,
    openBlockDialog,
    onChange,
  }: {
    blockIndex: number;
    classNames: { checkboxContainer: SxProps; checkbox: SxProps };
    value: PatrolTimetableValueBlock[][];
    saved: PatrolTimetableValueBlock[][] | null;
    version: number;
    comboValue: string | null;
    disabled?: boolean;
    openBlockDialog: (dayIndex: number, blockIndex: number, value: PatrolTimetableValueBlock) => void;
    onChange: (dayIndex: number, blockIndex: number, newValue: PatrolTimetableValueBlock) => void;
  }) => {
    const [blockValue, setBlockValue] = useState<PatrolTimetableValueBlock[] | null>(null);

    const { background: editedBackground, hover: editedHover } = useEditedColor();

    const handleCheckChange = useCallback(
      (dayIndex, checked) => {
        if (!blockValue || !comboValue) {
          return;
        }

        const newValue = cloneDeep(blockValue);
        newValue[dayIndex] = { checked };

        if (dayIndex === 0 && comboValue === "all-same") {
          for (let i = 1; i < 7; i++) {
            newValue[i] = clone(newValue[dayIndex]);
          }
        } else if (dayIndex === 0 && (comboValue === "weekdays+weekend" || comboValue === "weekdays")) {
          for (let i = 1; i < 5; i++) {
            newValue[i] = clone(newValue[dayIndex]);
          }
        } else if (dayIndex === 5 && (comboValue === "weekdays+weekend" || comboValue === "weekend")) {
          newValue[6] = clone(newValue[dayIndex]);
        }
        setBlockValue(newValue);
        onChange(dayIndex, blockIndex, newValue[dayIndex]);
      },
      [blockValue, comboValue, onChange, blockIndex]
    );

    const dayDisabled = useCallback(
      (dayIndex: number) => {
        if (!comboValue) {
          return false;
        }
        return (
          (comboValue === "all-same" && dayIndex > 0 && dayIndex < 7) ||
          (["weekdays+weekend", "weekdays"].includes(comboValue) && dayIndex > 0 && dayIndex < 5) ||
          (["weekdays+weekend", "weekend"].includes(comboValue) && dayIndex === 6)
        );
      },
      [comboValue]
    );

    useEffect(() => {
      const v = [] as PatrolTimetableValueBlock[];
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        v.push(clone(value[dayIndex][blockIndex]));
      }
      setBlockValue((old) => (isEqual(old, v) ? old : v));
    }, [value, blockIndex]);

    return (
      <>
        {Array.from({ length: 7 }).map((_day, dayIndex) => (
          <Grid item key={`day-${dayIndex}-block${blockIndex}`} sx={classNames.checkboxContainer}>
            <Box
              sx={
                blockValue &&
                blockValue[dayIndex].checked === (saved?.[dayIndex][blockIndex].checked) &&
                blockValue[dayIndex].tags === saved?.[dayIndex][blockIndex].tags &&
                blockValue[dayIndex].interval === saved?.[dayIndex][blockIndex].interval &&
                blockValue[dayIndex].offset === saved?.[dayIndex][blockIndex].offset &&
                blockValue[dayIndex].random === saved?.[dayIndex][blockIndex].random
                  ? {}
                  : {
                      backgroundColor: editedBackground,
                      "&.MuiBox-root:hover": {
                        backgroundColor: editedHover,
                      },
                    }
              }
            >
              <TTCheckbox
                sx={classNames.checkbox}
                value={blockValue ? blockValue[dayIndex].checked : false}
                onChange={(checked) => handleCheckChange(dayIndex, checked)}
                disabled={disabled || dayDisabled(dayIndex) || (blockValue ? blockValue[dayIndex].checked === null && !blockValue[dayIndex].block : false)}
              />
              {version === 2 ? (
                <IconButton
                  sx={{ margin: 0, padding: 0 }}
                  onClick={blockValue ? () => openBlockDialog(dayIndex, blockIndex, blockValue[dayIndex]) : undefined}
                  disabled={disabled || dayDisabled(dayIndex) || (blockValue ? blockValue[dayIndex].checked === null && !blockValue[dayIndex].block : false)}
                >
                  <AddIcon />
                </IconButton>
              ) : null}
            </Box>
          </Grid>
        ))}
      </>
    );
  }
);

const TTCheckbox = ({
  value,
  onChange,
  sx,
  disabled,
}: {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  sx: SxProps;
  disabled: boolean;
}) => {
  const [checked, setChecked] = useState(value ?? false);
  const [indeterminate, setIndeterminate] = useState(value === null);

  useEffect(() => {
    setChecked(value ?? false);
    setIndeterminate(value === null);
  }, [value]);

  const handleChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setChecked(checked);
    setIndeterminate(false);
    onChange(checked);
  };

  return <Checkbox sx={sx} checked={checked} indeterminate={indeterminate} onChange={handleChange} disabled={disabled} />;
};

const BlockDialog = memo(
  ({
    selectedBlock,
    onChange,
    open,
    onClose,
  }: {
    selectedBlock: SelectedBlock;
    onChange: (value: PatrolTimetableValueBlock) => void;
    open: boolean;
    onClose: () => void;
  }) => {
    const [block, setBlock] = useState(selectedBlock.value.block !== undefined ? `${selectedBlock.value.block}` : "");
    const [tags, setTags] = useState(selectedBlock.value.tags !== undefined ? `${selectedBlock.value.tags}` : "");
    const [interval, setInterval] = useState(selectedBlock.value.interval !== undefined ? `${selectedBlock.value.interval}` : "");
    const [offset, setOffset] = useState(selectedBlock.value.offset !== undefined ? `${selectedBlock.value.offset}` : "");
    const [random, setRandom] = useState(selectedBlock.value.random !== undefined ? `${selectedBlock.value.random}` : "");

    const { formatMessage } = useIntl();

    const okClick = useCallback(() => {
      const newValue: PatrolTimetableValueBlock = { checked: null, block: block ? parseInt(block, 10) : 1 };
      if (tags) {
        newValue.tags = parseInt(tags, 10);
      }
      if (interval) {
        newValue.interval = parseInt(interval, 10);
      }
      if (offset) {
        newValue.offset = parseInt(offset, 10);
      }
      if (random) {
        newValue.random = parseInt(random, 10);
      }
      onChange(newValue);
      onClose();
    }, [onChange, block, tags, interval, offset, random, onClose]);

    useEffect(() => {
      setBlock(selectedBlock.value.block !== undefined ? `${selectedBlock.value.block}` : "");
      setTags(selectedBlock.value.tags !== undefined ? `${selectedBlock.value.tags}` : "");
      setInterval(selectedBlock.value.interval !== undefined ? `${selectedBlock.value.interval}` : "");
      setOffset(selectedBlock.value.offset !== undefined ? `${selectedBlock.value.offset}` : "");
      setRandom(selectedBlock.value.random !== undefined ? `${selectedBlock.value.random}` : "");
    }, [selectedBlock]);

    return (
      <Dialog open={open} onClose={onClose} scroll="paper">
        <DialogTitle>{selectedBlock.title}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1} sx={{ flexDirection: "column" }}>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Blocks" })}
                placeholder="1"
                variant="filled"
                type="number"
                inputProps={{ min: 1 }}
                InputLabelProps={{ shrink: true }}
                value={block}
                onChange={(e) => {
                  setBlock(e.target.value);
                }}
                fullWidth
                size="small"
                sx={{ margin: 0 }}
                autoComplete="off"
              />
            </Grid>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Tags" })}
                placeholder={formatMessage({ defaultMessage: "Default" })}
                variant="filled"
                type="number"
                inputProps={{ min: 1 }}
                InputLabelProps={{ shrink: true }}
                value={tags}
                onChange={(e) => {
                  setTags(e.target.value);
                }}
                fullWidth
                size="small"
                sx={{ margin: 0 }}
                autoComplete="off"
              />
            </Grid>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Interval" })}
                placeholder={formatMessage({ defaultMessage: "Default" })}
                variant="filled"
                type="number"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
                value={interval}
                onChange={(e) => {
                  setInterval(e.target.value);
                }}
                fullWidth
                size="small"
                sx={{ margin: 0 }}
                autoComplete="off"
              />
            </Grid>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Offset" })}
                placeholder={formatMessage({ defaultMessage: "Default" })}
                variant="filled"
                type="number"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
                value={offset}
                onChange={(e) => {
                  setOffset(e.target.value);
                }}
                fullWidth
                size="small"
                sx={{ margin: 0 }}
                autoComplete="off"
              />
            </Grid>
            <Grid item>
              <TextField
                label={formatMessage({ defaultMessage: "Random" })}
                placeholder={formatMessage({ defaultMessage: "Default" })}
                variant="filled"
                type="number"
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
                value={random}
                onChange={(e) => {
                  setRandom(e.target.value);
                }}
                fullWidth
                size="small"
                sx={{ margin: 0 }}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{formatMessage({ defaultMessage: "Cancel" })}</Button>
          <Button onClick={okClick}>{formatMessage({ defaultMessage: "OK" })}</Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default OPTTV12;
